import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { RelocationSandbox } from 'app/core/sandboxes/relocation.sandbox';
import { CalendarDay, CalendarFlorderInfo } from 'app/core/store/calendar/common/calendar.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { logger } from 'app/core/util/logger';
import { CalendarPageContainer } from 'app/modules/calendar/common/calendar-page-container/calendar-page.container';
import { fadeInAnimation } from 'app/animations';
import * as fromRoot from 'app/core/store';
import * as relocationDetailActions from 'app/core/store/edit-relocation-detail/relocation-detail.actions';
import * as calendarActions from 'app/core/store/calendar/relocations/calendar-relocations.actions';
import * as templateActions from 'app/core/store/template/template.actions';
import { CalendarRelocations } from 'app/core/store/calendar/relocations/calendar-relocations.interface';
import { goAction } from 'app/core/store/router/router.actions';
import { takeWhile } from 'rxjs/operators';
import { getFirst$$ } from 'app/core/util/util';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../../core/util/layout-breakpoints';

@Component({
  selector: 'app-calendar-relocations-page',
  templateUrl: './calendar-relocations-page.container.html',
  styleUrls: ['../../common/calendar-page-container/calendar-page.container.scss'],
  animations: [fadeInAnimation],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarRelocationsPageContainer extends CalendarPageContainer<CalendarRelocations> implements OnInit {
  height: number = 0;

  isPortrait$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.portrait);
  isLandscape$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.landscape);

  constructor(public sandbox: RelocationSandbox, private breakpointObserver: BreakpointObserver) {
    super();
    logger.debug(`CalendarRelocationsPageContainer constructor`);

    this.store
      .select(state => state.calendar.relocations)
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((calendarRelocations: CalendarRelocations) => {
        this.flordersState = calendarRelocations;
        this.selectedTemplate = calendarRelocations.selectedTemplate;
        this.deliveryDaysRequesting = calendarRelocations.deliveryDaysRequesting;
        this.availableDays = calendarRelocations.availableDays;
      });

    this.store.dispatch(new relocationDetailActions.RelocationDetailClearAction());

    this.store
      .select('definitions')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((definitions: Definitions) => {
        this.places = definitions.places.reduce((res, place) => {
          return {
            ...res,
            [place.id]: place,
          };
        }, {});
      });
  }

  ngOnInit() {
    setTimeout(() => {
      this.resize(window.innerHeight);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.resize(event.currentTarget.innerHeight);
  }

  resize(innerHeight) {
    const newHeight = innerHeight - 205;
    if (this.height !== newHeight) {
      this.height = newHeight;
      this.cd.markForCheck();
    }
  }

  async loadFlorders(dates: { fromDate: string; toDate: string }) {
    this.filter = {
      unloadingDateFrom: dates.fromDate,
      unloadingDateTo: dates.toDate,
    };
    this.store.dispatch(
      new calendarActions.CalendarRelocationsLoadAction({
        filter: this.filter,
        definitions: await getFirst$$(this.sandbox.definitions$),
        places: this.places,
      })
    );
  }

  async showFlorderModal(day: CalendarDay) {
    this.sandbox.ShowCreateRelocationModalAction({
      day,
      templates: (await getFirst$$(this.sandbox.templateState$)).templates,
      filter: this.filter,
      definitions: await getFirst$$(this.sandbox.definitions$),
      places: this.places,
    });
  }

  createFlorder() {
    this.store.dispatch(goAction({ path: '/relocation/new' }));
  }

  openFlorder(relocation: CalendarFlorderInfo) {
    this.store.dispatch(
      goAction({
        path: [`relocation/${relocation.etmOrderNumber}`],
        query: {
          salesOrderNumber: relocation.salesOrderNumbers && relocation.salesOrderNumbers[0],
          back: 'calendar/relocation',
        },
      })
    );
  }

  createTemplate() {
    this.store.dispatch(
      goAction({
        path: ['/relocation/template/new'],
        query: { back: 'relocations' },
      })
    );
  }

  createFlorderFromTemplate(templateId: number, date: string) {
    this.sandbox.createRelocationFromTemplate(templateId, date);
  }

  deleteTemplate(templateId: number) {
    this.store.dispatch(new templateActions.TemplatesDeleteAction({ templateId, type: 'RELOCATION' }));
  }

  requestDeliveryDates() {
    if (this.selectedTemplate) {
      this.store.dispatch(new calendarActions.CalendarRelocationsRequestRelocationUnloadingDatesAction());
    }
  }

  selectTemplate(template) {
    this.store.dispatch(new calendarActions.CalendarRelocationsTemplateSelectedAction(template));

    // request delivery dates for selected template when it is selected
    // not when it is unselected
    if (this.selectedTemplate) {
      this.requestDeliveryDates();
    }
  }

  editTemplate(templateId: number) {
    this.store.dispatch(new templateActions.TemplatesStartEditAction({ templateId, type: 'RELOCATION' }));
  }
}
